/**
 * Video greeter CHROME — status overlay, summon, live-presence chip,
 * glow, and call controls around the avatar media. Two flavours:
 *
 *   - big   (sp-big-avatar) — skyscraper hero, ~200px tall above the
 *           message list; shrinks to 52px status bar once chat starts.
 *   - mini  (sp-mini-avatar) — 140px circle to the left of the input
 *           pill in INPUT layout.
 *
 * M4 (mobile UX program): the media elements NO LONGER live here. The
 * greeter <video> + live Simli <video> are owned by ONE persistent
 * avatar stage (`../avatar-stage.ts`) that re-parents between dock
 * anchors — this component renders the `.sp-avatar-dock` anchor and
 * registers it. A Preact re-render can therefore never recreate a
 * media element, and `liveReady` survives surface moves (invariant I3).
 * All media lifecycle effects (src swap, speaking-pause, stream attach
 * + first-frame handshake, visibility resume) moved to the stage.
 *
 * State model (all signals so every dock stays in lock-step):
 *   - greeterUrl    server-resolved video URL (null → fallback img)
 *   - speaking      TTS playback active — visualizer takes the surface
 *   - listening     mic recognizer active — adds --listening class
 *   - audioLevel    0..1 from Web Audio analyser, drives --audio-level
 *   - statusLabel   "Talk Now" / "Listening" / "Speaking" / etc.
 *   - chatStarted   big-only — adds --initialized to shrink the hero
 */

import type { Signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { SoundBars } from './sound-bars';
import type { AvatarStage, AvatarDockName } from '../avatar-stage';

export interface VideoGreeterBits {
  greeterUrl: Signal<string | null>;
  speaking: Signal<boolean>;
  listening: Signal<boolean>;
  audioLevel: Signal<number>;
  statusLabel: Signal<string>;
  /**
   * Live video-avatar stream (Simli) during a voice call. When set it
   * takes over the avatar surface as the real-time talking head, and
   * INVERTS the greeter's defaults: it stays visible WHILE speaking (the
   * lip-sync is the whole point) and the big avatar does NOT shrink on
   * chatStarted. Null → normal pre-recorded-greeter behavior. Optional so
   * non-voice callers can omit it.
   */
  liveStream?: Signal<MediaStream | null>;
  /**
   * Voice 'connecting' state — drives the summon/load overlay: the agent's
   * own still, blurred + shimmering with a pulsing glow, that resolves into
   * the live face the moment `liveStream` binds. Replaces the bare
   * connecting chip and dissolves the greeter-flash (the blurred still IS
   * the placeholder). Big avatar only. Optional so non-voice callers omit it.
   */
  connecting?: Signal<boolean>;
  /** Per-agent avatar still for the summon overlay (falls back to fallbackUrl). */
  posterUrl?: Signal<string>;
  /** Agent name for the arrival copy ("{name} is joining…"). */
  agentName?: Signal<string>;
  /**
   * Set by THIS component when the live <video> paints its first frame (via
   * requestVideoFrameCallback / 'playing'). The summon holds until this so the
   * face doesn't flash white between track-bound and first-rendered-frame.
   */
  liveReady?: Signal<boolean>;
  /**
   * Visitor's own mic mute state (from voiceState). Drives the mute control
   * in the live-call pill — muted shows a slashed mic + an accented tint.
   * Optional so non-voice callers omit it.
   */
  micMuted?: Signal<boolean>;
  /**
   * Live voice-state name ('listening' | 'thinking' | 'speaking' | …). Drives
   * the on-face identity chip's label and the avatar's glow state. Optional.
   */
  voiceStatus?: Signal<string>;
  /** Active-speaker audio level [0..1] — breathes the speaking glow. */
  voiceLevel?: Signal<number>;
}

export interface VideoGreeterProps {
  bits: VideoGreeterBits;
  size: 'big' | 'mini';
  /**
   * The shared avatar stage (M4). When provided, this component renders
   * a dock anchor and registers it under `dockName`; the stage re-parents
   * the persistent media elements into whichever dock is active.
   */
  stage?: AvatarStage;
  dockName?: AvatarDockName;
  /** Big avatar only — shrinks to status-bar mode once user starts chatting. */
  chatStarted?: Signal<boolean>;
  fallbackUrl: string;
  bars: { height: string; barWidth: string; gap: string };
  onStatusClick: (e: MouseEvent) => void;
  /**
   * Live-call controls (big avatar only). When onToggleMute + onEndCall are
   * supplied a compact control pill (mute · End) renders at the bottom edge of
   * the avatar while a live face streams, replacing the lone End button.
   * An audio-source picker is deferred to v2 (the call uses the OS-default
   * mic; the daily-session helpers are the seed). Omitted on the mini avatar
   * and on non-voice callers.
   */
  onToggleMute?: () => void;
  onEndCall?: () => void;
  /**
   * Minimize-to-PiP, and ONLY where the header cannot do it.
   *
   * This used to read "Minimize is intentionally NOT here — the header
   * minimizes and the widget has many mount positions." True in the sheet
   * and the panel. NOT true in the site-agent BAR, which hides the header
   * entirely (`[data-layout=input] .sp-header{display:none}`): a call
   * started from the bar had mute and End and no way down, so the co-pilot
   * PiP — built, styled, mounted and wired — was unreachable from the one
   * surface most calls begin on (prod 2026-10-24).
   *
   * So the original rule stands wherever a header exists; the caller passes
   * this only when one does not. Still absent on the mini avatar.
   */
  onMinimize?: () => void;
}

/** Stroke-icon mirror of widget-chrome's HeaderIcon — raw path markup. */
function CtrlIcon({ d, size = 19 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

const ICON_MIC =
  '<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>';
const ICON_MIC_OFF =
  '<line x1="2" y1="2" x2="22" y2="22"/><path d="M18.89 13.23A7 7 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" y1="19" x2="12" y2="23"/>';
/** Chevron-down: "put the call away", not the header's minus (which means
 *  "shrink this panel one tier"). Different gesture, different glyph. */
const ICON_MINIMIZE = '<polyline points="6 9 12 15 18 9"/>';
const ICON_HANGUP =
  '<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.07 4.18 2 2 0 0 1 5 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.59 9.91"/><line x1="22" y1="2" x2="2" y2="22"/>';

export function VideoGreeter({
  bits,
  size,
  stage,
  dockName,
  chatStarted,
  fallbackUrl,
  bars,
  onStatusClick,
  onToggleMute,
  onEndCall,
  onMinimize,
}: VideoGreeterProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const greeterUrl = bits.greeterUrl.value;
  const speaking = bits.speaking.value;
  const listening = bits.listening.value;
  const audioLevel = bits.audioLevel.value;
  const liveStream = bits.liveStream?.value ?? null;
  const hasLive = !!liveStream;
  // The face is actually PAINTING (first frame rendered), not just track-bound.
  // Controls/chip/glow + summon-end gate on THIS so they appear with the face,
  // never over a white warming-up card. Falls back to hasLive when no
  // liveReady signal is wired (mini avatar / non-voice callers).
  const liveReady = bits.liveReady?.value ?? hasLive;
  // While a live avatar is streaming it owns the surface: don't shrink on
  // chatStarted (it stays the hero) and don't morph to the visualizer on
  // speaking (the lip-sync IS the visual).
  const initialized = !!chatStarted?.value && !hasLive;
  const hasVideo = !!greeterUrl;
  // Summon/load overlay: shown on the big avatar while voice is connecting
  // and the live face hasn't bound yet. Kept mounted (opacity-toggled) so
  // it fades both in and out — the live video crossfades in beneath it.
  const connecting = bits.connecting?.value ?? false;
  const poster = bits.posterUrl?.value ?? fallbackUrl;
  // Hold the loading overlay until the live face actually PAINTS its first
  // frame — not just until 'connecting' clears. The Simli stream usually binds
  // (hasLive) a beat before frames render; without `|| hasLive` the summon
  // dismissed early and left a gap (white, or a fake still) before the video.
  // This keeps it loading → video with no gap.
  const summoning = (connecting || hasLive) && !liveReady && size === 'big';
  // Arrival copy advances in beats while summoning: identity → context.
  // Beat 0 = "{name} is joining…"; beat 1 = "Reading this page…" (the
  // labor-illusion beat — the agent really does receive page context).
  const [summonBeat, setSummonBeat] = useState(0);
  useEffect(() => {
    if (!summoning) {
      setSummonBeat(0);
      return;
    }
    const t = setTimeout(() => setSummonBeat(1), 1300);
    return () => clearTimeout(t);
  }, [summoning]);
  const micMuted = bits.micMuted?.value ?? false;
  // Live-call control pill: mute · End, anchored to the bottom edge of the
  // avatar (off the face). Replaces the lone End button once a live face is
  // streaming (the bottom-right End shoved onto the mouth — David's note).
  // Big avatar only, and only when the caller wired the handlers.
  // While summoning (connecting, or a stream bound but not yet painted) the
  // pill shows End alone. The visitor pressed the call button and could then
  // press nothing: in the bar that was ZERO controls on desktop and phone
  // (measured 2026-10-25), with the composer already narrowed for a pill
  // that had not rendered. Mute and Minimize wait for a live face — there
  // is no mic turn to mute yet and nothing to minimize to.
  const showControls =
    (liveReady || summoning) && size === 'big' && !!onEndCall && !!onToggleMute;
  const liveControls = showControls && liveReady;
  // Live presence: the on-face identity chip + which glow the avatar wears,
  // both derived from the voice-state name. 'live' is the neutral default
  // (face up, no active turn yet).
  const voiceStatus = bits.voiceStatus?.value ?? '';
  const liveState =
    voiceStatus === 'speaking'
      ? 'speaking'
      : voiceStatus === 'listening'
        ? 'listening'
        : voiceStatus === 'thinking'
          ? 'thinking'
          : 'live';
  const liveLabel =
    liveState === 'speaking'
      ? 'Speaking'
      : liveState === 'listening'
        ? 'Listening'
        : liveState === 'thinking'
          ? 'Thinking'
          : 'Live';
  const showLiveId = liveReady && size === 'big';
  const agentName = bits.agentName?.value?.trim() ?? '';
  const summonTitle = agentName
    ? `Connecting you to ${agentName}`
    : 'Connecting you…';
  // Subtitle reflects the actual load state (audio joins first, then the
  // video avatar binds) — status, not a feature claim.
  const summonSub =
    summonBeat === 0 ? 'Joining audio…' : 'Connecting video…';

  // M4 — register this surface as a dock on the shared avatar stage. The
  // stage owns the media elements + their whole imperative lifecycle;
  // this component is chrome around whichever dock is active.
  useEffect(() => {
    const anchor = dockRef.current;
    if (!stage || !dockName || !anchor) return;
    stage.registerDock(dockName, anchor);
    return () => stage.unregisterDock(dockName);
  }, [stage, dockName]);

  // Drive the --audio-level CSS variable on the root element. Done
  // imperatively (60-times-a-second updates would re-render Preact for
  // no reason).
  useEffect(() => {
    rootRef.current?.style.setProperty('--audio-level', audioLevel.toFixed(3));
  }, [audioLevel]);

  // Drive --voice-level for the live-call glow ring — the active speaker's
  // level (bot when speaking, visitor when listening). Imperative for the
  // same reason as --audio-level (per-frame, no re-render).
  const voiceLevel = bits.voiceLevel?.value ?? 0;
  useEffect(() => {
    rootRef.current?.style.setProperty('--voice-level', voiceLevel.toFixed(3));
  }, [voiceLevel]);

  const cls = [
    size === 'big' ? 'sp-big-avatar' : 'sp-mini-avatar',
    initialized && size === 'big' ? 'sp-big-avatar--initialized' : '',
    // Suppress the speaking visualizer-takeover while the live avatar is
    // on — the talking head replaces the sound bars.
    speaking && !hasLive && size === 'big' ? 'sp-big-avatar--speaking' : '',
    speaking && !hasLive && size === 'mini' ? 'sp-mini-avatar--speaking' : '',
    listening && size === 'big' ? 'sp-big-avatar--listening' : '',
    listening && size === 'mini' ? 'sp-mini-avatar--listening' : '',
    hasLive && size === 'big' ? 'sp-big-avatar--live' : '',
    hasLive && size === 'mini' ? 'sp-mini-avatar--live' : '',
    // Live presence glow state (big avatar only) — drives the brand ring.
    // Gate on liveReady so the glow appears WITH the painted face.
    liveReady && size === 'big' ? `sp-live-${liveState}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Visibility rules:
  //   live stream → live video owns the surface (greeter + fallback off,
  //                 stays visible even while speaking)
  //   speaking → greeter + fallback hidden (visualizer takes over)
  //   no greeter URL → greeter video hidden, fallback img shown
  //   greeter URL + idle → greeter video shown, fallback hidden
  // (Greeter/live video visibility is stage-owned now — see avatar-stage.ts.)
  const showFallback = !hasVideo && !speaking && !hasLive;
  // Big avatar hides the whole block when there's nothing to show —
  // legacy parity (avoid an empty 200px hero slot on agents with no
  // configured video). A live avatar always shows.
  const hideContainer = size === 'big' && !hasVideo && !hasLive && !summoning;
  const containerStyle = hideContainer ? 'display:none' : '';

  const statusCls =
    size === 'big' ? 'sp-big-avatar-status' : 'sp-mini-avatar-status';
  const btnCls = size === 'big' ? 'sp-big-avatar-btn' : 'sp-mini-avatar-btn';
  const textCls = size === 'big' ? 'sp-big-avatar-text' : 'sp-mini-avatar-text';

  return (
    <div ref={rootRef} class={cls} style={containerStyle}>
      {/* M4 — the avatar stage docks the persistent media elements here.
          Preact never renders children inside this anchor, so re-renders
          can't disturb (or recreate) the <video>s. */}
      <div ref={dockRef} class="sp-avatar-dock" />
      {/* Live presence glow — a brand-tinted inset ring that breathes with the
          active speaker (--voice-level). Steady-soft while listening, brighter
          + pulsing while speaking. Inset so it haloes the face, not the gutter. */}
      {liveReady && size === 'big' ? (
        <div class="sp-avatar-glow" aria-hidden="true" />
      ) : null}
      {/* On-face live-state chip (top-left) — the turn state (Listening /
          Speaking / Thinking), paired with the glow. Identity (name + title)
          lives in the header; this is state-only so nothing is said twice. */}
      {showLiveId ? (
        <div class={`sp-live-id sp-live-id--${liveState}`}>
          <span class="sp-live-dot" aria-hidden="true" />
          <span class="sp-live-state">{liveLabel}</span>
        </div>
      ) : null}
      {/* Arrival overlay — the agent "joining the meeting." The face in a
          gentle rack-focus (not a loading blur), a soft key light blooming
          up, named copy that advances identity→context, and ONE bounded
          accent progress line. Resolves into the live face (kept mounted,
          opacity-toggled, so it crossfades both ways). */}
      {size === 'big' ? (
        <div
          class={`sp-summon${summoning ? ' sp-summon--on' : ''}`}
          aria-hidden="true"
        >
          <div
            class="sp-summon-bg"
            style={`background-image:url(${poster})`}
          />
          <div class="sp-summon-center">
            <div class="sp-summon-card">
              <div class="sp-summon-spinner" />
              <div class="sp-summon-title">{summonTitle}</div>
              <div class="sp-summon-sub">{summonSub}</div>
            </div>
          </div>
        </div>
      ) : null}
      {size === 'mini' ? (
        <img
          src={fallbackUrl}
          alt=""
          style={showFallback ? '' : 'display:none'}
        />
      ) : null}
      <div class={statusCls}>
        <button
          type="button"
          class={btnCls}
          onClick={(e) => onStatusClick(e as unknown as MouseEvent)}
        >
          <SoundBars
            recording={listening}
            height={bars.height}
            barWidth={bars.barWidth}
            gap={bars.gap}
          />
          <span class={textCls}>{bits.statusLabel.value}</span>
        </button>
      </div>
      {showControls ? (
        <div class="sp-avatar-controls">
          {/* Compact glass pill: mute · End. */}
          <div
            class="sp-controls-pill"
            role="group"
            aria-label="Call controls"
          >
            {liveControls ? (
              <button
                type="button"
                class={`sp-ctrl sp-ctrl--mic${micMuted ? ' sp-ctrl--muted' : ''}`}
                aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
                aria-pressed={micMuted ? 'true' : 'false'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMute?.();
                }}
              >
                <CtrlIcon d={micMuted ? ICON_MIC_OFF : ICON_MIC} />
              </button>
            ) : null}
            {liveControls && onMinimize ? (
              <button
                type="button"
                class="sp-ctrl sp-ctrl--min"
                aria-label="Minimize call"
                onClick={(e) => {
                  e.stopPropagation();
                  onMinimize();
                }}
              >
                <CtrlIcon d={ICON_MINIMIZE} />
              </button>
            ) : null}
            <button
              type="button"
              class="sp-ctrl sp-ctrl--end"
              aria-label="End call"
              onClick={(e) => {
                e.stopPropagation();
                onEndCall?.();
              }}
            >
              <CtrlIcon d={ICON_HANGUP} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
